import React, { useEffect, useState } from "react";
import OrderedModuleEditor from "@/components/common/OrderedModuleEditor";
import MediaImageField from "@/components/common/MediaImageField";
import DragList from "@/components/common/DragList";
import { ImgWithFallback, resolveMediaUrl, serviceStaticUrl } from "@/lib/serviceMedia";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

const Field = ({ label, children, hint }) => (
  <div className="space-y-1">
    <Label className="text-xs text-white/50">{label}</Label>
    {children}
    {hint && <p className="text-[11px] text-white/40">{hint}</p>}
  </div>
);

function slugToTitle(slug) {
  return (slug || "")
    .split("-")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ")
    .replace(/ And /g, " & ");
}

function emptyProject() {
  return {
    title: "",
    year: "",
    client: "",
    desc: "",
    highlights: [],
    tags: [],
    media: null,
    cta_label: "",
    cta_href: "",
  };
}

function csv(list) {
  return Array.isArray(list) ? list.join(", ") : "";
}

function fromCsv(value) {
  return String(value || "")
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
}

function normalizeShowcase(item) {
  const projects = (item.projects || []).map((p) => ({
    title: p.title || "",
    year: p.year || "",
    client: p.client || "",
    desc: p.desc || "",
    highlights: p.highlights || [],
    tags: p.tags || [],
    media: p.media || null,
    cta_label: p.cta_label || p.cta?.label || "",
    cta_href: p.cta_href || p.cta?.href || "",
    category: p.category || item.service_slug || "",
  }));
  return {
    ...item,
    _originalSlug: item.service_slug || "",
    service_slug: item.service_slug || "",
    tagline: item.tagline || "",
    overview: item.overview || "",
    sectionTitle: item.sectionTitle || item.section_title || "",
    stats: item.stats || [],
    projects: projects.length ? projects : [emptyProject()],
    insight: item.insight || { title: "", body: "" },
    detailSection: item.detailSection || null,
    cta: item.cta || { label: "Request This Service", href: "/contact" },
  };
}

function prepareShowcasePayload(item) {
  const slug = (item.service_slug || "").trim();
  if (!slug) {
    throw new Error("Select a service before saving a showcase");
  }
  return {
    service_slug: slug,
    tagline: item.tagline || "",
    overview: item.overview || "",
    sectionTitle: item.sectionTitle || "",
    section_title: item.sectionTitle || "",
    order: item.order || 1,
    stats: (item.stats || []).map((s) => ({ value: s.value || "", label: s.label || "" })),
    projects: (item.projects || []).map((p) => ({
      title: p.title || `${slug} project`,
      category: slug,
      year: p.year || "",
      client: p.client || "",
      desc: p.desc || item.overview || "",
      highlights: p.highlights || [],
      tags: p.tags || [],
      media: p.media || null,
      cta_label: p.cta_label || "",
      cta_href: p.cta_href || "",
    })),
    insight: item.insight || { title: "", body: "" },
    ...(item.detailSection ? { detailSection: item.detailSection } : { detailSection: null }),
    cta: item.cta || { label: "Request This Service", href: "/contact" },
    status: item.status || "published",
  };
}

export default function ServiceShowcases() {
  const [services, setServices] = useState([]);

  useEffect(() => {
    api.get("/services?page_size=100")
      .then((r) => setServices((r.data.items || []).filter((s) => s.status === "published" || !s.status)))
      .catch(() => setServices([]));
  }, []);

  return (
    <OrderedModuleEditor
      resource="admin/site/service-showcases"
      singular="showcase"
      title="Service Showcases"
      subtitle="Drag to reorder showcases. Edit tagline, stats, projects, and CTAs in the inspector."
      titleKey="tagline"
      columnsClass="xl:grid-cols-[240px_1fr_400px]"
      inspectorScroll
      getApiId={(it) => it._originalSlug || it.service_slug}
      normalizeItem={normalizeShowcase}
      preparePayload={prepareShowcasePayload}
      enableStatus
      defaultValues={{
        order: 1,
        service_slug: "",
        tagline: "",
        overview: "",
        sectionTitle: "What We Offer",
        stats: [],
        projects: [emptyProject()],
        insight: { title: "", body: "" },
        detailSection: null,
        cta: { label: "Request This Service", href: "/contact" },
        status: "published",
      }}
      getItemLabel={(it, idx) => (it.service_slug ? slugToTitle(it.service_slug) : `Showcase ${idx + 1}`)}
      renderPreviewItem={(it) => {
        const project = it.projects?.[0];
        const fallback = serviceStaticUrl(it.service_slug);
        const src = resolveMediaUrl(project?.media?.src, fallback) || fallback;
        return (
          <div className="space-y-3">
            <div className="flex gap-3 items-start">
              {src ? (
                <ImgWithFallback
                  src={project?.media?.src}
                  fallback={fallback}
                  alt=""
                  className="w-20 h-14 rounded-lg object-cover shrink-0 bg-white/5"
                />
              ) : (
                <span className="w-20 h-14 rounded-lg glass flex items-center justify-center text-[10px] text-white/40 shrink-0">
                  No media
                </span>
              )}
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-widest text-white/40">
                  {it.service_slug ? slugToTitle(it.service_slug) : "Select a service"}
                </div>
                <div className="text-sm font-medium text-white mt-0.5 line-clamp-2">
                  {it.tagline || "Untitled showcase"}
                </div>
                <p className="text-xs text-white/50 mt-1 line-clamp-2">{it.overview || ""}</p>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-white/5 text-white/50">
                {(it.stats || []).length} stats
              </span>
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-white/5 text-white/50">
                {(it.projects || []).length} project{(it.projects || []).length === 1 ? "" : "s"}
              </span>
            </div>
            {(it.stats || []).length > 0 && (
              <div className="grid grid-cols-2 gap-2">
                {it.stats.slice(0, 4).map((s, i) => (
                  <div key={`${s.value}-${i}`} className="rounded-lg bg-white/[0.03] border border-white/10 px-2 py-1.5">
                    <div className="text-xs font-medium text-white truncate">{s.value || "—"}</div>
                    <div className="text-[10px] text-white/40 truncate">{s.label || ""}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      }}
      renderInspector={(item, patch, allItems) => {
        const isNew = String(item.id || "").startsWith("_new_");
        const taken = new Set(
          (allItems || [])
            .filter((it) => it.id !== item.id && it.service_slug)
            .map((it) => it.service_slug)
        );
        const fallback = serviceStaticUrl(item.service_slug);

        return (
          <>
            <Field label="Service" hint={isNew ? "Each service can have one showcase." : "Locked after create."}>
              {isNew ? (
                <select
                  value={item.service_slug || ""}
                  onChange={(e) => patch({ service_slug: e.target.value })}
                  className="w-full bg-black/20 border border-white/10 text-white rounded-md px-3 py-2 text-sm"
                >
                  <option value="">— Select a service —</option>
                  {services.map((s) => (
                    <option key={s.slug} value={s.slug} disabled={taken.has(s.slug)}>
                      {s.title}{taken.has(s.slug) ? " (exists)" : ""}
                    </option>
                  ))}
                </select>
              ) : (
                <Input
                  value={slugToTitle(item.service_slug)}
                  disabled
                  className="bg-black/20 border-white/10 text-white disabled:opacity-50"
                />
              )}
            </Field>

            <Field label="Tagline">
              <Input
                value={item.tagline || ""}
                onChange={(e) => patch({ tagline: e.target.value })}
                className="bg-black/20 border-white/10 text-white"
              />
            </Field>

            <Field label="Overview">
              <Textarea
                value={item.overview || ""}
                onChange={(e) => patch({ overview: e.target.value })}
                rows={4}
                className="bg-black/20 border-white/10 text-white"
              />
            </Field>

            <Field label="Section Title" hint="Heading above project cards">
              <Input
                value={item.sectionTitle || ""}
                onChange={(e) => patch({ sectionTitle: e.target.value })}
                placeholder="What We Offer"
                className="bg-black/20 border-white/10 text-white"
              />
            </Field>

            <div className="space-y-2 pt-1">
              <div className="flex items-center justify-between">
                <Label className="text-xs text-white/50">Stats</Label>
                <span className="text-[10px] text-white/35">Drag to reorder</span>
              </div>
              <DragList
                items={item.stats || []}
                onChange={(stats) => patch({ stats })}
                emptyText="No stats yet."
                addLabel="Add stat"
                onAdd={() => patch({ stats: [...(item.stats || []), { value: "", label: "" }] })}
                renderItem={(stat, _i, { update }) => (
                  <div className="grid grid-cols-1 gap-1.5">
                    <Input
                      value={stat.value || ""}
                      onChange={(e) => update({ value: e.target.value })}
                      placeholder="Value"
                      className="bg-black/20 border-white/10 text-white text-xs h-8"
                    />
                    <Input
                      value={stat.label || ""}
                      onChange={(e) => update({ label: e.target.value })}
                      placeholder="Label"
                      className="bg-black/20 border-white/10 text-white text-xs h-8"
                    />
                  </div>
                )}
              />
            </div>

            <div className="space-y-2 pt-1">
              <div className="flex items-center justify-between">
                <Label className="text-xs text-white/50">Projects</Label>
                <span className="text-[10px] text-white/35">Drag to reorder</span>
              </div>
              <DragList
                items={item.projects || []}
                onChange={(projects) => patch({ projects })}
                emptyText="No projects yet."
                addLabel="Add project"
                onAdd={() => patch({ projects: [...(item.projects || []), emptyProject()] })}
                renderItem={(project, _i, { update }) => (
                  <div className="space-y-1.5">
                    <Input
                      value={project.title || ""}
                      onChange={(e) => update({ title: e.target.value })}
                      placeholder="Project title"
                      className="bg-black/20 border-white/10 text-white text-xs h-8"
                    />
                    <Textarea
                      value={project.desc || ""}
                      onChange={(e) => update({ desc: e.target.value })}
                      placeholder="Description"
                      rows={2}
                      className="bg-black/20 border-white/10 text-white text-xs"
                    />
                    <div className="grid grid-cols-2 gap-1.5">
                      <Input
                        value={project.year || ""}
                        onChange={(e) => update({ year: e.target.value })}
                        placeholder="Year"
                        className="bg-black/20 border-white/10 text-white text-xs h-8"
                      />
                      <Input
                        value={project.client || ""}
                        onChange={(e) => update({ client: e.target.value })}
                        placeholder="Client"
                        className="bg-black/20 border-white/10 text-white text-xs h-8"
                      />
                    </div>
                    <Input
                      value={csv(project.highlights)}
                      onChange={(e) => update({ highlights: fromCsv(e.target.value) })}
                      placeholder="Highlights, comma-separated"
                      className="bg-black/20 border-white/10 text-white text-xs h-8"
                    />
                    <Input
                      value={csv(project.tags)}
                      onChange={(e) => update({ tags: fromCsv(e.target.value) })}
                      placeholder="Tags, comma-separated"
                      className="bg-black/20 border-white/10 text-white text-xs h-8"
                    />
                    <div className="grid grid-cols-2 gap-1.5">
                      <Input
                        value={project.cta_label || ""}
                        onChange={(e) => update({ cta_label: e.target.value })}
                        placeholder="CTA label"
                        className="bg-black/20 border-white/10 text-white text-xs h-8"
                      />
                      <Input
                        value={project.cta_href || ""}
                        onChange={(e) => update({ cta_href: e.target.value })}
                        placeholder="/contact"
                        className="bg-black/20 border-white/10 text-white text-xs h-8"
                      />
                    </div>
                    <MediaImageField
                      value={project.media}
                      onChange={(media) => update({ media })}
                      folder="showcases"
                      valueMode="media"
                      aspectClass="h-24"
                      fallbackSrc={fallback}
                      hint="Project image"
                    />
                  </div>
                )}
              />
            </div>

            <div className="grid grid-cols-2 gap-2">
              <Field label="CTA Label">
                <Input
                  value={item.cta?.label || ""}
                  onChange={(e) => patch({ cta: { ...(item.cta || {}), label: e.target.value } })}
                  className="bg-black/20 border-white/10 text-white"
                />
              </Field>
              <Field label="CTA URL">
                <Input
                  value={item.cta?.href || ""}
                  onChange={(e) => patch({ cta: { ...(item.cta || {}), href: e.target.value } })}
                  placeholder="/contact"
                  className="bg-black/20 border-white/10 text-white"
                />
              </Field>
            </div>

            <Field label="Insight Title">
              <Input
                value={item.insight?.title || ""}
                onChange={(e) => patch({ insight: { ...(item.insight || {}), title: e.target.value } })}
                className="bg-black/20 border-white/10 text-white"
              />
            </Field>
            <Field label="Insight Body">
              <Textarea
                value={item.insight?.body || ""}
                onChange={(e) => patch({ insight: { ...(item.insight || {}), body: e.target.value } })}
                rows={3}
                className="bg-black/20 border-white/10 text-white"
              />
            </Field>

            <div className="space-y-2 pt-1">
              <div className="flex items-center justify-between">
                <Label className="text-xs text-white/50">Detail Section</Label>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="text-white/70 h-7 text-xs"
                  onClick={() =>
                    patch({
                      detailSection: item.detailSection ? null : { title: "", items: [] },
                    })
                  }
                >
                  {item.detailSection ? "Remove" : "Add"}
                </Button>
              </div>
              {item.detailSection && (
                <>
                  <Input
                    value={item.detailSection.title || ""}
                    onChange={(e) =>
                      patch({ detailSection: { ...item.detailSection, title: e.target.value } })
                    }
                    placeholder="Section title"
                    className="bg-black/20 border-white/10 text-white text-xs h-8"
                  />
                  <DragList
                    items={item.detailSection.items || []}
                    onChange={(items) =>
                      patch({ detailSection: { ...item.detailSection, items } })
                    }
                    emptyText="No detail items."
                    addLabel="Add item"
                    onAdd={() =>
                      patch({
                        detailSection: {
                          ...item.detailSection,
                          items: [...(item.detailSection.items || []), ""],
                        },
                      })
                    }
                    renderItem={(value, _i, { updateValue }) => (
                      <Input
                        value={value || ""}
                        onChange={(e) => updateValue(e.target.value)}
                        placeholder="Detail item"
                        className="bg-black/20 border-white/10 text-white text-xs h-8"
                      />
                    )}
                  />
                </>
              )}
            </div>
          </>
        );
      }}
    />
  );
}
